import { findDate } from './dates.js';
import { findRrule } from './rrule.js';
import { masquerLiens } from '../linkify.js';

export type TokenType = 'project' | 'date' | 'rrule';

export interface Token {
  type: TokenType;
  start: number;
  end: number;
  text: string;
}

export interface ParseResult {
  title: string;
  projectName: string | null;
  dueDate: string | null;
  rrule: string | null;
  tokens: Token[];
}

export function parseQuickAdd(input: string, today: string): ParseResult {
  const tokens: Token[] = [];
  // Rien ne se cherche dans un lien : `…#ancre` n'est pas un projet, `…/today/…` pas une date.
  // Les positions trouvées dans le masque valent pour `input` (même longueur).
  const texte = masquerLiens(input);

  // 1. Project
  let projectName: string | null = null;
  const projMatch = texte.match(/#([\p{L}\p{N}_-]+)/u);
  if (projMatch) {
    projectName = projMatch[1]!;
    tokens.push({
      type: 'project',
      start: projMatch.index!,
      end: projMatch.index! + projMatch[0].length,
      text: projMatch[0],
    });
  }

  // 2. RRULE
  const rruleMatch = findRrule(texte);
  if (rruleMatch) {
    tokens.push({
      type: 'rrule',
      start: rruleMatch.start,
      end: rruleMatch.end,
      text: rruleMatch.raw,
    });
  }

  // 3. Date — skip if it would overlap an already-captured token (e.g. "every thu"
  // captures as rrule; we don't want to also highlight "thu" as a date inside it).
  const dateMatch = findDate(texte, today);
  const overlaps = dateMatch
    ? tokens.some(
        (t) => dateMatch.start < t.end && dateMatch.end > t.start
      )
    : false;
  if (dateMatch && !overlaps) {
    tokens.push({
      type: 'date',
      start: dateMatch.start,
      end: dateMatch.end,
      text: dateMatch.raw,
    });
  }

  // Build title by removing token spans (sorted desc so indices stay valid)
  const sorted = [...tokens].sort((a, b) => b.start - a.start);
  let title = input;
  for (const t of sorted) {
    title = title.slice(0, t.start) + title.slice(t.end);
  }
  title = title.replace(/\s+/g, ' ').trim();

  return {
    title,
    projectName,
    dueDate: dateMatch && !overlaps ? dateMatch.iso : null,
    rrule: rruleMatch?.rrule ?? null,
    tokens,
  };
}
